import { Box, Button, Chip, IconButton, Typography } from "@mui/material"
import { Visibility, VisibilityOff } from "@mui/icons-material"
import type { SxProps, Theme } from "@mui/material"
import CopyableTextField from "components/copyable-text-field"
import type { PatType, PersonalAccessToken } from "../../types"

export const TYPE_LABEL: Record<PatType, string> = {
  read: "read-only token",
  read_write: "read-write token",
}

const TYPE_DESCRIPTION: Record<PatType, string> = {
  read: "Grants read-only API access with your entitlements.",
  read_write: "Grants read and write API access, capped at your entitlements.",
}

const ROW_SX = { py: 2, borderBottom: "1px solid", borderColor: "divider" } satisfies SxProps<Theme>
const DESCRIPTION_SX = { mb: 1 } satisfies SxProps<Theme>
const REVEAL_LINE_SX = { display: "flex", alignItems: "center", gap: 1 } satisfies SxProps<Theme>
const SECRET_FIELD_SX = { flexGrow: 1 } satisfies SxProps<Theme>
const MASKED_SX = { fontFamily: "monospace" } satisfies SxProps<Theme>
const CHIP_SX = { mt: 1 } satisfies SxProps<Theme>
const META_SX = { display: "block", mt: 1 } satisfies SxProps<Theme>
const ACTIONS_SX = { mt: 1, display: "flex", gap: 1 } satisfies SxProps<Theme>

interface PatTokenRowProps {
  type: PatType
  token: PersonalAccessToken | undefined
  canCreate: boolean
  revealedSecret: string | undefined
  disabled: boolean
  onCreate: (type: PatType) => void
  onReveal: (token: PersonalAccessToken) => void
  onRotate: (token: PersonalAccessToken) => void
  onRevoke: (token: PersonalAccessToken) => void
}

export default function PatTokenRow({
  type,
  token,
  canCreate,
  revealedSecret,
  disabled,
  onCreate,
  onReveal,
  onRotate,
  onRevoke,
}: PatTokenRowProps) {
  const label = TYPE_LABEL[type]

  return <Box sx={ROW_SX}>
    <Typography variant="subtitle1">{label.replace(/^\w/, character => character.toUpperCase())}</Typography>
    <Typography variant="body2" color="text.secondary" sx={DESCRIPTION_SX}>{TYPE_DESCRIPTION[type]}</Typography>

    {!token && canCreate && (
      <Button variant="contained" size="small" disabled={disabled} onClick={() => onCreate(type)}>
        {`Create ${label}`}
      </Button>
    )}

    {token && <Box>
      <Box sx={REVEAL_LINE_SX}>
        <Box sx={SECRET_FIELD_SX}>
          {revealedSecret
            ? <CopyableTextField id={`pat-${token.id}`} label={null} value={revealedSecret} useValueChangeEffect={false} />
            : <Typography sx={MASKED_SX}>{`${token.prefix}••••••••`}</Typography>}
        </Box>
        <IconButton
          aria-label={`${revealedSecret ? "Hide" : "Reveal"} ${label}`}
          onClick={() => onReveal(token)}
          size="small"
          disabled={disabled}
        >
          {revealedSecret ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </Box>
      {token.source === "system" && <Chip size="small" color="info" label="Created automatically for Agent Chat" sx={CHIP_SX} />}
      <Typography variant="caption" color="text.secondary" sx={META_SX}>
        {`Created ${new Date(token.createdAt).toLocaleDateString()}`}
        {token.lastUsedAt ? ` · Last used ${new Date(token.lastUsedAt).toLocaleString()}` : " · Never used"}
      </Typography>
      <Box sx={ACTIONS_SX}>
        <Button size="small" disabled={disabled} onClick={() => onRotate(token)}>Rotate</Button>
        <Button size="small" color="error" disabled={disabled} onClick={() => onRevoke(token)}>Revoke</Button>
      </Box>
    </Box>}
  </Box>
}
